import { create } from 'zustand';
import type { ExternalDevice, ExternalPort } from '@livepuls/types';
import { api } from '../lib/api.js';
import { getSocket } from '../lib/socket.js';

interface ExternalsState {
  ports: ExternalPort[];
  devices: ExternalDevice[];
  loading: boolean;
  lastError: string | null;
  load(): Promise<void>;
  bind(): () => void;
  createDevice(input: {
    name: string;
    micPortKey?: string;
    spkPortKey?: string;
    pgmPortKey?: string;
    pgmVolume?: number;
    web?: boolean;
  }): Promise<ExternalDevice>;
  updateDevice(
    id: string,
    patch: {
      name?: string;
      micPortKey?: string | null;
      spkPortKey?: string | null;
      pgmPortKey?: string | null;
      pgmVolume?: number | null;
    },
  ): Promise<ExternalDevice>;
  removeDevice(id: string): Promise<void>;
}

interface PortsResponse {
  ports: ExternalPort[];
}

export const useExternalsStore = create<ExternalsState>((set, get) => ({
  ports: [],
  devices: [],
  loading: false,
  lastError: null,

  async load() {
    set({ loading: true, lastError: null });
    try {
      const [p, d] = await Promise.all([
        api.get<PortsResponse>('/api/v1/ports'),
        api.get<ExternalDevice[]>('/api/v1/external-devices'),
      ]);
      set({ ports: p.ports, devices: d, loading: false });
    } catch (err) {
      set({ loading: false, lastError: (err as Error).message });
    }
  },

  bind() {
    const socket = getSocket();
    const onPorts = (e: { ports: ExternalPort[] }): void => set({ ports: e.ports });
    const onDevices = (e: { devices: ExternalDevice[] }): void => set({ devices: e.devices });
    socket.on('ports:update', onPorts);
    socket.on('external-devices:update', onDevices);
    void get().load();
    return () => {
      socket.off('ports:update', onPorts);
      socket.off('external-devices:update', onDevices);
    };
  },

  async createDevice(input) {
    const created = await api.post<ExternalDevice>('/api/v1/external-devices', input);
    // Idempotent: das Backend feuert zusätzlich `external-devices:update` mit der
    // vollen Liste (Socket). Nur hinzufügen, wenn nicht bereits vorhanden – sonst
    // erscheint das Gerät doppelt (Race zwischen Socket-Event und diesem set).
    set((s) =>
      s.devices.some((d) => d.id === created.id)
        ? {}
        : { devices: [...s.devices, created].sort((a, b) => a.name.localeCompare(b.name)) },
    );
    return created;
  },

  async updateDevice(id, patch) {
    const updated = await api.put<ExternalDevice>(`/api/v1/external-devices/${id}`, patch);
    set((s) => ({
      devices: s.devices.map((d) => (d.id === id ? updated : d)),
    }));
    return updated;
  },

  async removeDevice(id) {
    await api.delete(`/api/v1/external-devices/${id}`);
    set((s) => ({ devices: s.devices.filter((d) => d.id !== id) }));
  },
}));
